import React, { useCallback, useContext, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import Tooltip from '@totem/components/common/tooltip/Tooltip';
import { sortStringAscending } from '@totem/components/services/threats/eventsContainer_legacy/utilities';
import NinjaOneContext from '@totem/components/settings/sections/ninjaOneMapping/NinjaOneContext';
import NinjaOneLocationMappingTable from '@totem/components/settings/sections/ninjaOneMapping/NinjaOneLocationMappingTable';
import { TenantData } from '@totem/components/settings/sections/ninjaOneMapping/reportTypes';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';
import { NINJA_ONE_ENDPOINT } from '@totem/utilities/endpoints';

const styles = {
  iconButton: {
    margin: '0 2px',
  },
};
const NinjaOneMappingTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, loading, onAction, onActionComplete } =
    useContext(NinjaOneContext);
  const [isSending, setIsSending] = useState(false);

  const sendTenantDelete = useCallback(
    async (apiConfigId: string, tenantId: number) => {
      if (isSending) {
        return;
      }

      setIsSending(true);

      fetch(`${NINJA_ONE_ENDPOINT}/tenants/${apiConfigId}/${tenantId}`, {
        method: 'DELETE',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => {
          if (res.status < 400) {
            onActionComplete(true);
          } else {
            notification.error({
              title: 'Error tenant delete!',
              description: 'An error occurred while deleting tenant.',
              duration: 0,
            });

            onActionComplete(false);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [isSending],
  );

  const onDelete = (tenant: TenantData) => {
    sendTenantDelete(tenant.apiConfigId, tenant.tenant.tenantId);
  };

  const columns: ColumnProps<TenantData>[] = [
    {
      title: 'Tenant Name (In NinjaOne)',
      dataIndex: 'tenant.tenantName',
      key: 'tenant.tenantName',
      sorter: (compA, compB) =>
        sortStringAscending(compA.tenant.tenantName, compB.tenant.tenantName),
      render: (text: string, item: TenantData) => {
        return item.tenant.tenantName;
      },
    },
    {
      title: 'Description',
      dataIndex: 'tenant.description',
      key: 'tenant.description',
      sorter: (compA, compB) =>
        sortStringAscending(compA.tenant.description, compB.tenant.description),
      render: (text: string, item: TenantData) => {
        return item.tenant.description;
      },
    },
    {
      title: 'Building',
      dataIndex: 'building.id',
      key: 'building.id',
      sorter: (compA, compB) =>
        sortStringAscending(compA.building.name, compB.building.name),
      render: (text: string, item: TenantData) => {
        return typeof item.building !== 'undefined' && item.building !== null
          ? item.building.name
          : '';
      },
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'action',
      render: (_, item: TenantData) => (
        <div className="center-table-cell">
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Update Building Mapping" placement="top">
              <Button
                icon={<EditOutlined />}
                shape="circle"
                onClick={(): void => onAction('showMappingDialog', item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
          {permissions.systemAdministrator.hasPermissions(userProfile) && (
            <Tooltip title="Delete Tenant" placement="top">
              <Button
                icon={<IBDIcon name={'Delete'} />}
                shape="circle"
                onClick={(): void => onDelete(item)}
                style={styles.iconButton}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={typeof data !== 'undefined' && data !== null ? data : []}
      loading={loading}
      rowKey={(record) => record.tenant.tenantId}
      pagination={{
        showSizeChanger: true,
      }}
      expandable={{
        rowExpandable: (record: TenantData) =>
          isNotNull(record.tenant.locations) &&
          record.tenant.locations.length > 0,
        expandedRowRender: (record) => (
          <NinjaOneLocationMappingTable
            tenant={record}
            locations={record.tenant.locations.sort((compA, compB) =>
              sortStringAscending(compA.locationName, compB.locationName),
            )}
          />
        ),
        defaultExpandAllRows: false,
      }}
    />
  );
};

export default NinjaOneMappingTable;
